/**
 * SORA2 视频生成节点 - 支持文生视频和图生视频
 */
import React, { memo, useState, useEffect, useCallback } from 'react';
import { useReactFlow, useStore, Handle, Position } from '@xyflow/react';
import { nanoid } from 'nanoid';
import { useProject } from '../providers/ProjectProvider';
import { useNodeOperations } from '../providers/NodeOperationsProvider';
import ApiService from '../../../api/ApiService';
import LocalDataService from '../../../api/LocalDataService';
import { toast } from 'react-toastify';
import './Sora2VideoNode.css';

const apiService = new ApiService();

// 工具函数：判断数组是否相等
const arraysEqual = (a = [], b = []) => {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) {
      return false;
    }
  }
  return true;
};

// 工具函数：合并图片列表（保持原有顺序）
const mergeImageLists = (prev = [], next = []) => {
  const nextQueue = [...next];
  const reordered = [];

  prev.forEach((url) => {
    const index = nextQueue.indexOf(url);
    if (index !== -1) {
      reordered.push(url);
      nextQueue.splice(index, 1);
    }
  });

  return [...reordered, ...nextQueue];
};

// 模型配置（按价格优先排序）
const modelOptions = [
  // 标清模型（0.1元/次）
  { value: 'sora_video2-portrait', label: '竖屏标清 10秒', orientation: 'portrait', duration: '10s', quality: 'sd' },
  { value: 'sora_video2-landscape', label: '横屏标清 10秒', orientation: 'landscape', duration: '10s', quality: 'sd' },
  { value: 'sora_video2-portrait-15s', label: '竖屏标清 15秒', orientation: 'portrait', duration: '15s', quality: 'sd' },
  { value: 'sora_video2-landscape-15s', label: '横屏标清 15秒', orientation: 'landscape', duration: '15s', quality: 'sd' },
  // 高清模型（1.8元/次）
  { value: 'sora_video2-portrait-hd', label: '竖屏高清 10秒', orientation: 'portrait', duration: '10s', quality: 'hd' },
  { value: 'sora_video2-landscape-hd', label: '横屏高清 10秒', orientation: 'landscape', duration: '10s', quality: 'hd' },
  { value: 'sora_video2-portrait-hd-15s', label: '竖屏高清 15秒', orientation: 'portrait', duration: '15s', quality: 'hd' },
  { value: 'sora_video2-landscape-hd-15s', label: '横屏高清 15秒', orientation: 'landscape', duration: '15s', quality: 'hd' },
  { value: 'sora_video2-portrait-hd-25s', label: '竖屏高清 25秒', orientation: 'portrait', duration: '25s', quality: 'hd' },
  { value: 'sora_video2-landscape-hd-25s', label: '横屏高清 25秒', orientation: 'landscape', duration: '25s', quality: 'hd' },
];

export const Sora2VideoNode = memo(({ id, data }) => {
  const { updateNodeData, getNode, getEdges } = useReactFlow();
  const { addNodesWithEdges, duplicateNode, createNodeAndConnect } = useNodeOperations();
  const edges = useStore((state) => state.edges);
  const nodes = useStore((state) => state.nodes); // 添加节点监听
  const { readOnly } = useProject();
  
  const [model, setModel] = useState(data.model || 'sora_video2-portrait');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [connectedImages, setConnectedImages] = useState([]);

  useEffect(() => {
    updateNodeData(id, {
      model,
    });
  }, [id, model, updateNodeData]);

  // 获取连接到此节点的所有文本输入节点
  const getConnectedPromptNodes = () => {
    const edges = getEdges();
    const connectedEdges = edges.filter(edge => {
      if (edge.target !== id) return false;
      
      // 新数据：有明确的 targetHandle
      if (edge.targetHandle === 'prompt') return true;
      
      // 旧数据兼容：没有 targetHandle，根据源节点类型判断
      if (!edge.targetHandle) {
        const sourceNode = getNode(edge.source);
        return sourceNode?.type === 'promptInput';
      }
      
      return false;
    });
    
    const prompts = [];
    connectedEdges.forEach(edge => {
      const sourceNode = getNode(edge.source);
      if (sourceNode && sourceNode.type === 'promptInput' && sourceNode.data?.prompt) {
        prompts.push(sourceNode.data.prompt);
      }
    });
    
    return prompts;
  };

  // 获取连接到此节点的图片参考节点和任务节点
  const getConnectedImageNodes = useCallback(() => {
    const connectedEdges = edges.filter(edge => {
      if (edge.target !== id) return false;
      
      // 新数据：有明确的 targetHandle
      if (edge.targetHandle === 'image') return true;
      
      // 旧数据兼容：没有 targetHandle，根据源节点类型判断
      if (!edge.targetHandle) {
        const sourceNode = getNode(edge.source);
        const validImageTypes = ['imageReference', 'drawing', 'imageTask', 'videoTask'];
        return validImageTypes.includes(sourceNode?.type);
      }
      
      return false;
    });
    
    console.log(`Sora2VideoNode ${id} - 连接到图片输入的边:`, connectedEdges.length);
    
    const imageUrls = [];
    connectedEdges.forEach(edge => {
      const sourceNode = getNode(edge.source);
      console.log(`Sora2VideoNode ${id} - 源节点:`, sourceNode?.type, sourceNode?.data);
      
      // 从图片参考节点获取图片
      if (sourceNode && sourceNode.type === 'imageReference' && sourceNode.data?.imageUrl) {
        const url = sourceNode.data.imageUrl;
        if (!url.startsWith('data:') && !url.startsWith('blob:')) {
          imageUrls.push(url);
          console.log(`Sora2VideoNode ${id} - 从图片参考节点获取图片:`, url);
        }
      }
      
      // 从手绘节点获取图片（只使用已上传到云端的图片）
      if (sourceNode && sourceNode.type === 'drawing' && sourceNode.data?.imageUrl) {
        const url = sourceNode.data.imageUrl;
        // 只使用已上传到云端的图片，过滤掉本地 base64 预览
        if (!url.startsWith('data:') && !url.startsWith('blob:')) {
          imageUrls.push(url);
          console.log(`Sora2VideoNode ${id} - 从手绘节点获取图片:`, url);
        } else if (sourceNode.data?.isLocalPreview) {
          console.warn(`Sora2VideoNode ${id} - 手绘节点图片为本地预览，跳过`);
        }
      }
      
      // 从图片任务节点获取生成的图片（取第一张）
      if (sourceNode && sourceNode.type === 'imageTask') {
        console.log(`Sora2VideoNode ${id} - 任务节点数据:`, {
          status: sourceNode.data?.status,
          generatedImages: sourceNode.data?.generatedImages,
          imagesCount: sourceNode.data?.generatedImages?.length
        });
        
        if (sourceNode.data?.generatedImages && sourceNode.data.generatedImages.length > 0) {
          const firstImage = sourceNode.data.generatedImages[0];
          const url = typeof firstImage === 'string' ? firstImage : firstImage.url;
          if (url && !url.startsWith('data:') && !url.startsWith('blob:')) {
            imageUrls.push(url);
            console.log(`Sora2VideoNode ${id} - 从任务节点获取第一张图片:`, url);
          }
        }
      }
      
      // 从视频任务节点获取生成的视频封面
      if (sourceNode && sourceNode.type === 'videoTask') {
        if (sourceNode.data?.coverImageUrl) {
          const url = sourceNode.data.coverImageUrl;
          if (!url.startsWith('data:') && !url.startsWith('blob:')) {
            imageUrls.push(url);
            console.log(`Sora2VideoNode ${id} - 从视频任务节点获取封面:`, url);
          }
        }
      }
    });
    
    console.log(`Sora2VideoNode ${id} - 总共获取到 ${imageUrls.length} 张图片`);
    return imageUrls;
  }, [edges, nodes, getNode, id]);

  // 监听边的变化，更新连接的图片列表
  useEffect(() => {
    const imageUrls = getConnectedImageNodes();
    setConnectedImages((prev) => {
      const merged = mergeImageLists(prev, imageUrls);
      return arraysEqual(prev, merged) ? prev : merged;
    });
  }, [getConnectedImageNodes]);

  const handleDuplicate = (e) => {
    e.stopPropagation();
    duplicateNode(id);
  };

  const handleGenerate = async () => {
    try {
      setError(null);
      setIsGenerating(true);
      
      // 1. 获取连接的提示词
      const prompts = getConnectedPromptNodes();
      if (prompts.length === 0) {
        setError('请先连接文本输入节点');
        setIsGenerating(false);
        return;
      }
      
      const prompt = prompts.join('\n'); // 合并多个提示词
      console.log('使用提示词:', prompt);
      
      // 2. 获取连接的参考图片（可选）- 使用已连接的图片列表
      const imgUrl = connectedImages.length > 0 ? connectedImages[0] : null; // SORA2 只支持单张图片
      
      if (connectedImages.length > 1) {
        toast.warning('SORA2 只支持单张参考图片，将使用第一张图片');
      }
      
      console.log('使用参考图片:', imgUrl);
      
      // 3. 获取用户token
      const userData = LocalDataService.load_user_data();
      if (!userData || !userData.token) {
        setError('请先登录');
        toast.error('请先登录');
        setIsGenerating(false);
        return;
      }
      
      // 4. 调用 SORA2 API 创建任务
      const inputParams = {
        prompt: prompt,
        model: model,
      };
      
      // 如果有参考图片，添加到参数中
      if (imgUrl) {
        inputParams.img_url = imgUrl;
      }
      
      const response = await apiService.request(
        `${apiService.apiBase}/fal/tasks/create`,
        'POST',
        {
          token: userData.token,
          user_type: 1,
          app_name: 'st-ai/sora2',
          task_type: 'coze_api',
          input_params: inputParams
        }
      );
      
      console.log('SORA2 任务创建响应:', response);
      
      if (response.code === 200 && response.data) {
        const newTaskId = response.data.task_id;
        toast.success(imgUrl ? '图生视频任务已创建！' : '文生视频任务已创建！');
        
        // 5. 获取当前节点位置，创建任务展示节点在右侧
        const currentNode = getNode(id);
        const newNodePosition = {
          x: currentNode.position.x + 450, // 在右侧450px处
          y: currentNode.position.y,
        };
        
        // 6. 创建 VideoTaskNode 节点
        const taskNodeId = nanoid();
        const taskNode = {
          id: taskNodeId,
          type: 'videoTask',
          position: newNodePosition,
          data: {
            taskId: newTaskId,
            taskType: imgUrl ? 'image-to-video' : 'text-to-video',
            status: 'pending',
            inputParams: inputParams,
            videoUrl: null,
          },
        };
        
        // 7. 使用统一方法创建节点和连接边
        const newEdge = {
          id: nanoid(),
          source: id, // 当前SORA2视频节点
          target: taskNodeId, // 新创建的任务节点
          type: 'default',
        };
        
        // 使用统一的添加节点+连线方法
        await addNodesWithEdges([taskNode], [newEdge]);
        console.log('已创建视频任务节点和连接线:', taskNodeId);
        
        setIsGenerating(false);
      } else {
        throw new Error(response.message || '创建任务失败');
      }
      
    } catch (err) {
      console.error('SORA2 视频生成失败:', err);
      const errorMessage = err?.data?.detail || err?.message || '生成失败，请重试';
      setError(errorMessage);
      toast.error(errorMessage);
      setIsGenerating(false);
    }
  };

  // 验证连接是否有效
  const isValidConnection = useCallback((connection) => {
    // 如果连接到文本输入handle，只允许文本输入节点
    if (connection.targetHandle === 'prompt') {
      const sourceNode = getNode(connection.source);
      return sourceNode?.type === 'promptInput';
    }
    
    // 如果连接到图片输入handle
    if (connection.targetHandle === 'image') {
      const sourceNode = getNode(connection.source);
      const validImageTypes = ['imageReference', 'drawing', 'imageTask', 'videoTask'];
      return validImageTypes.includes(sourceNode?.type);
    }
    
    return true;
  }, [getNode]);

  // 处理 Handle 标签点击 - 自动创建节点并连接
  const handleHandleLabelClick = useCallback((e, handleId, handleType) => {
    e.stopPropagation();  // 阻止事件冒泡
    
    // 只读模式下禁用
    if (readOnly) {
      toast.info('只读模式下无法创建节点');
      return;
    }
    
    // 调用统一的创建方法（会自动从配置读取限制）
    createNodeAndConnect(id, 'sora2Video', handleId, handleType);
  }, [id, readOnly, createNodeAndConnect]);

  return (
    <div className="Sora2VideoNode-container">
      {/* 复制按钮 */}
      <button 
        className="Sora2VideoNode-duplicate-button"
        onClick={handleDuplicate}
        title="复制节点"
      >
        <i className="bi bi-copy"></i>
      </button>

      {/* 文本输入 Handle */}
      <Handle
        type="target"
        position={Position.Left}
        id="prompt"
        className="Sora2VideoNode-handle Sora2VideoNode-handle-prompt"
        isValidConnection={isValidConnection}
        style={{ top: '25%' }}
      >
        <div 
          className="Sora2VideoNode-handle-label Sora2VideoNode-handle-label-clickable"
          onClick={(e) => handleHandleLabelClick(e, 'prompt', 'text')}
          title="点击自动创建文本输入节点"
        >
          文本
        </div>
      </Handle>

      {/* 图片输入 Handle */}
      <Handle
        type="target"
        position={Position.Left}
        id="image"
        className="Sora2VideoNode-handle Sora2VideoNode-handle-image"
        isValidConnection={isValidConnection}
        style={{ top: '75%' }}
      >
        <div 
          className="Sora2VideoNode-handle-label Sora2VideoNode-handle-label-clickable"
          onClick={(e) => handleHandleLabelClick(e, 'image', 'image')}
          title="点击自动创建图片参考节点（最多1张）"
        >
          图片
        </div>
      </Handle>

      {/* 输出 Handle */}
      <Handle
        type="source"
        position={Position.Right}
        className="Sora2VideoNode-handle Sora2VideoNode-handle-output"
      />

      {/* 标题栏 */}
      <div className="Sora2VideoNode-title">SORA2 视频生成</div>

      {/* 内容区域 */}
      <div className="Sora2VideoNode-wrapper">
        {/* 模型选择 */}
        <div className="Sora2VideoNode-field">
          <label className="Sora2VideoNode-label">视频模型</label>
          <select
            value={model}
            onChange={(e) => setModel(e.target.value)}
            disabled={isGenerating || readOnly}
            className="Sora2VideoNode-select"
          >
            {modelOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* 模型信息 */}
        <div className="Sora2VideoNode-info">
          {(() => {
            const selectedModel = modelOptions.find(m => m.value === model);
            return (
              <>
                <span className="Sora2VideoNode-info-badge">
                  {selectedModel?.orientation === 'portrait' ? '📱 竖屏' : '🖥️ 横屏'}
                </span>
                <span className="Sora2VideoNode-info-badge">
                  ⏱️ {selectedModel?.duration}
                </span>
                <span className="Sora2VideoNode-info-badge">
                  {selectedModel?.quality === 'hd' ? '✨ 高清' : '💫 标清'}
                </span>
              </>
            );
          })()}
        </div>

        {/* 参考图片展示 */}
        {connectedImages.length > 0 && (
          <div className="Sora2VideoNode-field">
            <label className="Sora2VideoNode-label">
              参考图片 ({connectedImages.length > 1 ? '将使用第1张' : '1张'})
            </label>
            <div className="Sora2VideoNode-image-grid nodrag">
              {connectedImages.map((imageUrl, index) => (
                <div
                  key={`${imageUrl}-${index}`}
                  className={`Sora2VideoNode-image-thumb nodrag ${
                    index > 0 ? 'Sora2VideoNode-image-inactive' : ''
                  }`}
                >
                  <img 
                    src={imageUrl} 
                    alt={`参考图 ${index + 1}`}
                    className="Sora2VideoNode-thumb-img"
                  />
                  <div className="Sora2VideoNode-thumb-index">
                    {index + 1}
                  </div>
                  {index === 0 && (
                    <div className="Sora2VideoNode-thumb-active-badge">
                      ✓
                    </div>
                  )}
                </div>
              ))}
            </div>
            {connectedImages.length > 1 && (
              <div className="Sora2VideoNode-warning">
                ⚠️ SORA2 只支持单张参考图片，将使用第 1 张
              </div>
            )}
          </div>
        )}

        {/* 连接提示 */}
        <div className="Sora2VideoNode-hint">
          💡 <strong>文生视频</strong>：连接文本输入节点<br />
          🖼️ <strong>图生视频</strong>：额外连接图片参考节点/任务节点（最多1张）<br />
          点击生成后会自动创建视频任务节点展示结果
        </div>

        {/* 生成按钮 */}
        <button
          onClick={handleGenerate}
          disabled={isGenerating || readOnly}
          className={`Sora2VideoNode-generate ${isGenerating || readOnly ? 'Sora2VideoNode-generate-disabled' : ''}`}
        >
          {isGenerating ? '创建中...' : '创建视频任务'}
        </button>

        {/* 错误提示 */}
        {error && (
          <div className="Sora2VideoNode-error">
            {error}
          </div>
        )}
      </div>
    </div>
  );
});

Sora2VideoNode.displayName = 'Sora2VideoNode';

